import { Users, TrendingUp, Scale } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import cornellBlockchainLogo from "@/assets/cornell-blockchain-logo.jpg";
import cornellLogo from "@/assets/cornell-logo.jpg";

const organizations = [
  {
    title: "Head of Education",
    organization: "Cornell Blockchain Club",
    icon: Users,
    logo: cornellBlockchainLogo,
    description:
      "Taught Intro to Blockchain (CS 1998) to 200+ students. Led 6 TAs and launched first-ever labs on wallets, NFTs, and DEXs, fostering hands-on learning in Web3 fundamentals.",
  },
  {
    title: "Accelerator Member",
    organization: "Cornell Blockchain Accelerator",
    icon: TrendingUp,
    logo: cornellBlockchainLogo,
    description:
      "Contributed to raising $15M+ for blockchain startups. Participated in strategic initiatives that led to merger with IC3, strengthening Cornell's position in blockchain research and innovation.",
  },
  {
    title: "Board Member",
    organization: "Cornell CALS Academic Integrity Hearing Board",
    icon: Scale,
    logo: cornellLogo,
    description:
      "Serve on judicial board reviewing academic integrity cases. Evaluate evidence, conduct hearings, and make fair decisions on academic misconduct allegations to uphold Cornell's honor code.",
  },
];

const OrganizationCard = ({ org, index }: { org: (typeof organizations)[0]; index: number }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 },
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, []);

  const Icon = org.icon;

  return (
    <div
      ref={cardRef}
      className={`transition-all duration-350 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="bg-card border-2 rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow duration-200 ease-out">
        <div className="flex items-center gap-4 mb-4">
          {org.logo ? (
            <img
              src={org.logo}
              alt={`${org.organization} logo`}
              className="w-14 h-14 rounded-lg object-contain bg-white p-1 flex-shrink-0"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Icon className="h-7 w-7 text-white" />
            </div>
          )}
          <div>
            <h3 className="text-xl font-bold">{org.title}</h3>
            <p className="text-primary font-semibold">{org.organization}</p>
          </div>
        </div>
        <p className="text-muted-foreground leading-relaxed">{org.description}</p>
      </div>
    </div>
  );
};

const OrganizationsSection = () => {
  return (
    <section id="organizations" className="py-20">
      <div className="container mx-auto px-4">
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">Organizations & Activities</h2>
        <p className="text-center text-muted-foreground mb-16 max-w-2xl mx-auto">
          Leading blockchain education and innovation at Cornell
        </p>

        <div className="max-w-4xl mx-auto space-y-12">
          {organizations.map((org, index) => (
            <OrganizationCard key={index} org={org} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default OrganizationsSection;
